/**
 * One function per endpoint the frontend uses (§31's table).
 *
 * Each parses its response through the matching Zod schema, so a shape change
 * in the backend surfaces here — at the seam — rather than three components
 * deep as `undefined is not an object`.
 */

import { api, AI_TIMEOUT_MS, type RequestOptions } from './client';
import { ApiError } from './errors';

import * as s from './schemas';
import type { paths } from '@/types/api.generated';

/**
 * Parse, or fail with a code the UI already knows how to render (§44:
 * "If schema invalid, show safe fallback and log telemetry").
 */
function parse<T>(schema: { safeParse: (v: unknown) => { success: boolean; data?: T; error?: unknown } }, value: unknown, where: string): T {
  const result = schema.safeParse(value);
  if (!result.success || result.data === undefined) {
    console.error('[api] response failed validation', { where, error: result.error });
    throw new ApiError({
      code: 'MALFORMED_RESPONSE',
      status: 200,
      details: { where },
    });
  }
  return result.data;
}

// --- auth -------------------------------------------------------------------
export const authApi = {
  /** One account type: the sign-in screen no longer asks who you are. */
  requestMagicLink: async (identifier: string) =>
    parse(
      s.magicLinkResponseSchema,
      await api.post('/v1/auth/magic-link', { provider: 'whatsapp', identifier }, { anonymous: true }),
      'auth.magic-link',
    ),

  exchange: async (token: string) =>
    parse(
      s.exchangeResponseSchema,
      await api.post('/v1/auth/exchange', { token }, { anonymous: true }),
      'auth.exchange',
    ),

  refresh: async (refreshToken: string) =>
    parse(
      s.sessionTokensSchema,
      await api.post('/v1/auth/refresh', { refresh_token: refreshToken }, { anonymous: true }),
      'auth.refresh',
    ),

  signOut: async () => {
    await api.post('/v1/auth/sign-out');
  },
};

// --- student ----------------------------------------------------------------
export const studentApi = {
  profile: async () =>
    parse(s.studentProfileSchema, await api.get('/v1/students/me/profile'), 'student.profile'),

  createProfile: async (input: {
    display_name?: string | null;
    class_level: number;
    board_code: string;
    preferred_language: string;
    timezone?: string;
  }) =>
    parse(
      s.studentProfileSchema,
      await api.put('/v1/students/me/profile', input),
      'student.profile.create',
    ),

  updateProfile: async (input: Record<string, unknown>) =>
    parse(
      s.studentProfileSchema,
      await api.patch('/v1/students/me/profile', input),
      'student.profile.update',
    ),

  mastery: async () =>
    parse(s.masterySummarySchema, await api.get('/v1/students/me/mastery'), 'student.mastery'),

  skill: async (skillId: string) =>
    parse(
      s.skillProgressDetailSchema,
      await api.get(`/v1/students/me/skills/${skillId}`),
      'student.skill',
    ),

  gamification: async () =>
    parse(s.gamificationSchema, await api.get('/v1/students/me/gamification'), 'student.gamification'),

  recommendations: async (refresh = false) =>
    parse(
      s.recommendationSchema.array(),
      await api.get(`/v1/students/me/recommendations${refresh ? '?refresh=true' : ''}`),
      'student.recommendations',
    ),

  clickRecommendation: async (id: string) => {
    await api.post(`/v1/students/me/recommendations/${id}/click`);
  },

  activity: async (limit = 20) =>
    parse(
      s.activityItemSchema.array(),
      await api.get(`/v1/students/me/activity?limit=${limit}`),
      'student.activity',
    ),
};

// --- curriculum -------------------------------------------------------------
export const curriculumApi = {
  browse: async (params?: { board_code?: string; class_level?: number }) => {
    const query = new URLSearchParams();
    if (params?.board_code) query.set('board_code', params.board_code);
    if (params?.class_level) query.set('class_level', String(params.class_level));
    const suffix = query.toString() ? `?${query}` : '';
    return parse(s.curriculumSchema, await api.get(`/v1/curriculum${suffix}`), 'curriculum');
  },

  skill: async (skillId: string) =>
    parse(s.skillDetailSchema, await api.get(`/v1/curriculum/skills/${skillId}`), 'curriculum.skill'),
};

// --- learn ------------------------------------------------------------------
/**
 * The conversation about one syllabus skill.
 *
 * Kept across visits, like a homework thread — a child who asked three good
 * questions last week should find them rather than a blank box.
 */
export const learnApi = {
  chat: async (skillId: string) =>
    parse(s.topicChatSchema, await api.get(`/v1/learn/skills/${skillId}/chat`), 'learn.chat'),

  ask: async (skillId: string, text: string) =>
    parse(
      s.topicTurnSchema,
      await api.post(`/v1/learn/skills/${skillId}/chat`, { text }),
      'learn.ask',
    ),

  // No `kind` in the body: the path says which table this is, so there is
  // nothing for a caller to get wrong.
  narrate: async (turnId: string) =>
    parse(
      s.narrationSchema,
      await api.post(`/v1/learn/turns/${turnId}/narration`),
      'learn.narrate',
    ),
};

// --- billing ----------------------------------------------------------------
export const billingApi = {
  status: async () =>
    parse(s.subscriptionStatusSchema, await api.get('/v1/billing/status'), 'billing.status'),

  /** Only the tier this family can buy — the server decides which. */
  plans: async () =>
    parse(s.plansSchema, await api.get('/v1/billing/plans'), 'billing.plans'),

  /**
   * Start a checkout. Returns something to pay WITH, not a payment.
   *
   * Nobody is subscribed when this resolves: the row moves to active when
   * Razorpay's webhook says money moved, which is why the page polls status
   * afterwards rather than believing the callback it just received.
   */
  checkout: async (planCode: string) =>
    parse(
      s.checkoutSchema,
      await api.post('/v1/billing/checkout', { plan_code: planCode }),
      'billing.checkout',
    ),
};

// --- homework ---------------------------------------------------------------
export const homeworkApi = {
  createSession: async () =>
    parse(s.homeworkSessionSchema, await api.post('/v1/homework/sessions'), 'homework.session'),

  presign: async (input: { kind: 'image' | 'audio'; mime_type: string; size_bytes: number }) =>
    parse(s.presignSchema, await api.post('/v1/homework/assets/presign', input), 'homework.presign'),

  submit: async (
    input: {
      session_id: string;
      input_type: 'text' | 'image' | 'audio';
      text?: string;
      asset_id?: string;
      language?: string;
    },
    idempotencyKey: string,
  ) =>
    parse(
      s.submissionSchema,
      await api.post('/v1/homework/submissions', input, { idempotencyKey }),
      'homework.submit',
    ),

  /**
   * Tell the backend which question on the photographed page to work on.
   *
   * The submission id that comes back is NOT always the one passed in:
   * choosing a second question off the same photo starts a new submission, so
   * the caller must follow `submission_id` from the result rather than keep
   * polling the one it asked about.
   */
  selectQuestion: async (submissionId: string, questionId: string) =>
    parse(
      s.submissionSchema,
      await api.post(`/v1/homework/submissions/${submissionId}/select`, {
        question_id: questionId,
      }),
      'homework.selectQuestion',
    ),

  status: async (submissionId: string, options?: RequestOptions) =>
    parse(
      s.submissionSchema,
      await api.get(`/v1/homework/submissions/${submissionId}`, options),
      'homework.status',
    ),

  response: async (submissionId: string) =>
    parse(
      s.homeworkResponseSchema,
      await api.get(`/v1/homework/submissions/${submissionId}/response`, { timeoutMs: AI_TIMEOUT_MS }),
      'homework.response',
    ),

  retry: async (submissionId: string) =>
    api.post<{ submission_id: string; status: string }>(
      `/v1/homework/submissions/${submissionId}/retry`,
    ),

  /** Answer the tutor's question. Right or wrong, this opens the working. */
  answer: async (submissionId: string, answer: string) =>
    parse(
      s.answerResultSchema,
      await api.post(`/v1/homework/submissions/${submissionId}/answer`, { answer }),
      'homework.answer',
    ),

  /**
   * See the working without answering. A gate with no way through is a wall.
   *
   * The reason travels with it because the two ways of not answering mean
   * opposite things about the child: `gave_up` is stuck and needs easier
   * questions, `skipped` understood and did not want the exercise. The
   * backend keeps them apart, and it can only do that if we say which.
   */
  reveal: async (submissionId: string, reason: s.RevealReason = 'gave_up') =>
    parse(
      s.answerResultSchema,
      await api.post(`/v1/homework/submissions/${submissionId}/reveal`, { reason }),
      'homework.reveal',
    ),

  /**
   * One model call against context we already hold, so it is not a job.
   *
   * A photo makes it slower — OCR runs inline — and only when there is one.
   */
  askDoubt: async (
    submissionId: string,
    input: { text?: string; input_type?: 'text' | 'image' | 'audio'; asset_id?: string },
  ) =>
    parse(
      s.doubtSchema,
      await api.post(`/v1/homework/submissions/${submissionId}/doubts`, input, {
        timeoutMs: AI_TIMEOUT_MS,
      }),
      'homework.doubt',
    ),

  /**
   * Read a reply out loud. Synthesised on the first press and kept, so the
   * second and third listen cost nothing — which is what the feature is for.
   */
  narrate: async (submissionId: string, message: { kind: 'response' | 'doubt'; message_id: string }) =>
    parse(
      s.narrationSchema,
      await api.post(`/v1/homework/submissions/${submissionId}/audio`, message, {
        timeoutMs: AI_TIMEOUT_MS,
      }),
      'homework.narrate',
    ),
};

// --- practice ---------------------------------------------------------------
/**
 * Where a practice set came from, as the backend's enum spells it.
 *
 * Typed from the generated OpenAPI types rather than re-declared as `string`:
 * a plain string let a plausible-looking `'self_selected'` compile and then
 * 422 at runtime on every practice start. Now the names live in one place and
 * a rename on the backend fails the build.
 */
export type PracticeSource = NonNullable<
  paths['/v1/practice/sessions']['post']['requestBody']
>['content']['application/json']['source'];

export const practiceApi = {
  start: async (input: {
    skill_id: string;
    source?: PracticeSource;
    source_ref_id?: string | null;
    question_count?: number;
  }) =>
    parse(
      s.practiceSessionSchema,
      // Generation is a model call; §46 allows longer than a normal read.
      await api.post('/v1/practice/sessions', input, { timeoutMs: AI_TIMEOUT_MS }),
      'practice.start',
    ),

  /**
   * Show your working on a live question and ask.
   *
   * Not an attempt, and not graded. A separate call from `submitAttempt` for
   * the same reason it is a separate route: the scanner can misread every
   * digit here and cost the child nothing.
   */
  help: async (questionId: string, input: { asset_id?: string; asked?: string }) =>
    parse(
      s.practiceHelpSchema,
      await api.post(`/v1/practice/questions/${questionId}/help`, input, {
        timeoutMs: AI_TIMEOUT_MS,
      }),
      'practice.help',
    ),

  session: async (sessionId: string) =>
    parse(
      s.practiceSessionSchema,
      await api.get(`/v1/practice/sessions/${sessionId}`),
      'practice.session',
    ),

  submitAttempt: async (
    questionId: string,
    input: { answer: string; hints_used?: number; time_ms?: number },
    idempotencyKey: string,
  ) =>
    parse(
      s.attemptResultSchema,
      await api.post(`/v1/practice/questions/${questionId}/attempts`, input, {
        idempotencyKey,
        timeoutMs: AI_TIMEOUT_MS,
      }),
      'practice.attempt',
    ),
};

// --- parent -----------------------------------------------------------------
export const parentApi = {
  children: async () =>
    parse(s.childSummarySchema.array(), await api.get('/v1/parents/me/students'), 'parent.children'),

  progress: async (studentId: string) =>
    parse(
      s.parentProgressSchema,
      await api.get(`/v1/parents/students/${studentId}/progress`),
      'parent.progress',
    ),

  /** Profile, PIN state and children in one call — the parent app branches on all three. */
  overview: async () =>
    parse(s.parentOverviewSchema, await api.get('/v1/parents/me'), 'parent.overview'),

  saveProfile: async (input: {
    display_name?: string | null;
    preferred_language?: 'en' | 'hinglish';
    timezone?: string;
  }) => parse(s.parentOverviewSchema, await api.put('/v1/parents/me', input), 'parent.profile'),

  setPin: async (input: { pin: string; current_pin?: string }) => {
    await api.put('/v1/parents/me/pin', input);
  },

  addChild: async (input: {
    display_name: string;
    class_level: number;
    board_code: string;
    preferred_language: 'en' | 'hinglish';
    relationship?: 'guardian' | 'mother' | 'father' | 'other';
  }) =>
    parse(
      s.childSummarySchema,
      await api.post('/v1/parents/me/students', input),
      'parent.addChild',
    ),

  /**
   * The child's own way in (§17).
   *
   * One reusable link per child, sent to the parent's WhatsApp. Sending
   * again issues a new one and kills the old, which is also how a parent
   * kills a link that went somewhere it should not have.
   */
  childLink: async (studentId: string) =>
    parse(
      s.childLinkSchema,
      await api.get(`/v1/parents/me/children/${studentId}/sign-in-link`),
      'parent.childLink',
    ),

  sendChildLink: async (studentId: string) =>
    parse(
      s.childLinkSchema,
      await api.post(`/v1/parents/me/children/${studentId}/sign-in-link`),
      'parent.sendChildLink',
    ),

  revokeChildLink: async (studentId: string) => {
    await api.del(`/v1/parents/me/children/${studentId}/sign-in-link`);
  },
};

/**
 * Who is using the device (§17).
 *
 * Every one of these works from a parent's session and from a child's, which
 * is the point: a phone on a kitchen table gets picked up by whoever has
 * homework, and the second child should not need a parent to hand it over.
 */
export const profilesApi = {
  list: async () => parse(s.profileChoicesSchema, await api.get('/v1/profiles'), 'profiles.list'),

  /** No PIN. See the picker's own note on what that trades away. */
  enterChild: async (studentId: string) =>
    parse(
      s.enterProfileSchema,
      await api.post(`/v1/profiles/children/${studentId}/enter`),
      'profiles.enterChild',
    ),

  enterParent: async (pin: string) =>
    parse(
      s.enterProfileSchema,
      await api.post('/v1/profiles/parent/enter', { pin }),
      'profiles.enterParent',
    ),
};

// --- analytics --------------------------------------------------------------
export const eventsApi = {
  ingest: async (events: Array<Record<string, unknown>>) => {
    await api.post('/v1/events', { events });
  },
};
